import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Shield, Info, Loader2, GitCompareArrows } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CONF_COLORS = {
  High: { bg: "bg-emerald-500", text: "text-emerald-400", bar: "from-emerald-500/70 to-emerald-400/50" },
  Medium: { bg: "bg-blue-500", text: "text-blue-400", bar: "from-blue-500/70 to-blue-400/50" },
  Low: { bg: "bg-amber-500", text: "text-amber-400", bar: "from-amber-500/70 to-amber-400/50" },
  "Very Low": { bg: "bg-red-500", text: "text-red-400", bar: "from-red-500/70 to-red-400/50" },
};

export function HourlyConfidenceChart({ lat, lon, isDarkMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const fetchData = useCallback(async () => {
    if (!lat || !lon) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/weather/enhanced/hourly-confidence`, { params: { lat, lon } });
      setData(res.data);
    } catch (err) {
      console.error("Hourly confidence error:", err.message);
      setError("Forecast confidence is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-white/50" data-testid="hourly-confidence-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#00f2ea]" />
        Loading forecast confidence...
      </div>
    );
  }

  if (error) {
    return <div role="alert" className="text-sm text-amber-300" data-testid="hourly-confidence-error">{error}</div>;
  }

  if (!data || !data.hours?.length) {
    return <div className="text-sm text-white/45" data-testid="hourly-confidence-empty">No confidence timeline available yet.</div>;
  }

  const hours = data.hours || [];
  const maxConf = 100;

  // Show first 16 entries (48 hours in 3hr increments)
  const displayed = hours.slice(0, 16);
  const comparedHours = displayed.filter((hour) => hour.source_comparison);
  const strongAgreement = comparedHours.filter((hour) => hour.source_comparison?.agreement === "Strong").length;
  const agreementPct = comparedHours.length ? Math.round((strongAgreement / comparedHours.length) * 100) : null;

  return (
    <div data-testid="hourly-confidence-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-white/80 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#00f2ea]" />
          Forecast Confidence Timeline
        </h3>
        <div className="flex items-center gap-3">
          {agreementPct !== null && (
            <span className="flex items-center gap-1 text-[10px] text-cyan-300" data-testid="hourly-confidence-source-agreement">
              <GitCompareArrows className="w-3 h-3" />
              {agreementPct}% source agreement
            </span>
          )}
          {["High", "Medium", "Low", "Very Low"].map((level) => (
            <span key={`legend-${level}`} className={`flex items-center gap-1 text-[10px] ${CONF_COLORS[level]?.text || "text-white/40"}`}>
              <span className={`w-2 h-2 rounded-full ${CONF_COLORS[level]?.bg || "bg-white/20"}`} />
              {level}
            </span>
          ))}
        </div>
      </div>

      {/* Confidence Bar Chart */}
      <div className="relative">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {[100, 75, 50, 25].map((v) => (
            <div key={`gridline-${v}`} className="flex items-center gap-2 w-full">
              <span className={`text-[9px] w-6 text-right ${isDarkMode ? "text-white/15" : "text-gray-300"}`}>{v}%</span>
              <div className={`flex-1 h-px ${isDarkMode ? "bg-white/5" : "bg-gray-100"}`} />
            </div>
          ))}
        </div>

        <div className="flex items-end gap-[3px] h-40 ml-8 relative">
          {displayed.map((h, i) => {
            const height = (h.confidence / maxConf) * 100;
            const colors = CONF_COLORS[h.confidence_label] || CONF_COLORS["Low"];
            const isHovered = hoveredIdx === i;

            return (
              <div
                key={`conf-${h.dt}`}
                className="flex-1 h-full flex items-end justify-center relative"
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                data-testid={`hourly-confidence-hour-${h.dt}`}
              >
                {/* Hover tooltip */}
                {isHovered && (
                  <div className="absolute -top-20 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
                    <div className={`rounded-lg px-3 py-2 text-[10px] whitespace-nowrap shadow-xl ${isDarkMode ? "bg-[#111827] border border-white/10" : "bg-white shadow-lg border border-gray-200"}`}>
                      <p className="font-bold">{h.time} — {h.date}</p>
                      <p>{h.temp}°F — {h.description}</p>
                      <p>Rain: {h.rain_chance}% | Wind: {h.wind_speed}mph</p>
                      {h.source_comparison && (
                        <p className="mt-1 text-cyan-300">
                          OWM vs Open-Meteo: {h.source_comparison.agreement} ({h.source_comparison.confidence_adjustment} pts)
                        </p>
                      )}
                      <p className={`font-bold mt-1 ${colors.text}`}>Confidence: {h.confidence}% ({h.confidence_label})</p>
                    </div>
                  </div>
                )}

                {/* Bar */}
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                  className={`w-full rounded-t-sm cursor-pointer transition-all bg-gradient-to-t ${colors.bar} ${isHovered ? "opacity-100 ring-1 ring-white/20" : "opacity-80"}`}
                  style={{ minHeight: "4px" }}
                  data-testid={`hourly-confidence-bar-${h.dt}`}
                />
                {h.source_comparison && (
                  <span
                    className={`absolute -top-2 w-2 h-2 rounded-full border ${
                      h.source_comparison.agreement === "Strong" ? "bg-cyan-300 border-cyan-100" :
                        h.source_comparison.agreement === "Moderate" ? "bg-amber-300 border-amber-100" : "bg-red-300 border-red-100"
                    }`}
                    title={`${h.source_comparison.agreement} source agreement`}
                    data-testid={`hourly-confidence-source-dot-${h.dt}`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Time Labels */}
        <div className="flex gap-[3px] ml-8 mt-1.5">
          {displayed.map((h, i) => (
            <div key={`tlabel-${h.dt}`} className="flex-1 text-center">
              {i % 2 === 0 && (
                <p className={`text-[8px] font-mono ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>
                  {h.time.replace(" ", "")}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {comparedHours.length > 0 && (
        <div className={`mt-4 grid grid-cols-3 gap-2 text-center text-[10px] ${isDarkMode ? "text-white/45" : "text-gray-500"}`} data-testid="hourly-confidence-source-summary">
          <div className="rounded-lg bg-white/[0.04] py-2">
            <span className="block font-bold text-cyan-300">{strongAgreement}</span>
            Strong matches
          </div>
          <div className="rounded-lg bg-white/[0.04] py-2">
            <span className="block font-bold text-amber-300">{comparedHours.filter((hour) => hour.source_comparison?.agreement === "Moderate").length}</span>
            Moderate
          </div>
          <div className="rounded-lg bg-white/[0.04] py-2">
            <span className="block font-bold text-red-300">{comparedHours.filter((hour) => hour.source_comparison?.agreement === "Mixed").length}</span>
            Mixed
          </div>
        </div>
      )}

      {/* Confidence explanation */}
      <div className={`flex items-start gap-2 mt-4 p-3 rounded-xl text-xs ${isDarkMode ? "bg-white/[0.03] text-white/40" : "bg-gray-50 text-gray-500"}`} data-testid="hourly-confidence-note">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#00f2ea]/50" />
        <span>{data.note}</span>
      </div>
    </div>
  );
}
